import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * 🎯 COMPOSANT INVISIBLE - SIMULATEUR DE RECHERCHE SAUVEGARDÉE POUR CATÉGORIES
 * 
 * Ce composant reproduit EXACTEMENT le mécanisme des recherches sauvegardées
 * quand l'utilisateur clique sur une catégorie dans CategoryCarousel.
 * 
 * Il détecte les paramètres URL spéciaux et appelle les mêmes callbacks
 * que SavedSearchDropdown, permettant au système existant de fonctionner
 * naturellement sans aucune modification.
 */
const CategorySearchSimulator = ({ onApplySearch, onError }) => {
  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const applySavedSearch = searchParams.get('applySavedSearch');
    const categoryId = searchParams.get('categoryId');
    const categoryName = searchParams.get('categoryName');

    // 🎯 DÉTECTION : Si c'est une sélection de catégorie depuis CategoryCarousel
    if (applySavedSearch === 'category' && categoryId) {
      console.log('🚀 [CategorySearchSimulator] Détection sélection catégorie:', {
        categoryId,
        categoryName,
        mechanism: 'simulate_saved_search'
      });

      // ⭐ REPRODUCTION EXACTE DU MÉCANISME DES RECHERCHES SAUVEGARDÉES
      // Créer l'objet filtre au même format que les vraies recherches sauvegardées
      const categoryFilters = {
        categoryIds: [categoryId] // Format exact attendu par AdvancedFiltersStructured
      };

      // Créer un objet searchData fictif identique aux vraies recherches sauvegardées
      const mockSearchData = {
        id: `category_${categoryId}`,
        name: categoryName || 'Catégorie sélectionnée',
        filters: categoryFilters,
        isFromCategory: true // Marqueur pour distinguer des vraies recherches sauvegardées
      };

      // 🔥 APPEL EXACT DU MÊME CALLBACK QUE SavedSearchDropdown
      // Cela déclenche handleApplySavedSearch dans ResponsiveFilterSidebar
      // qui appelle setExternalFilters + handleFiltersChange automatiquement
      console.log('🎯 [CategorySearchSimulator] Application via onApplySearch (comme SavedSearchDropdown):', {
        filters: categoryFilters,
        searchData: mockSearchData
      });

      // Attendre que les composants soient montés puis appliquer
      setTimeout(() => {
        try {
          if (onApplySearch) {
            console.log('✅ [CategorySearchSimulator] Déclenchement onApplySearch');
            onApplySearch(categoryFilters, mockSearchData);
          } else {
            console.warn('⚠️ [CategorySearchSimulator] onApplySearch non disponible');
            onError?.('Impossible d\'appliquer la sélection de catégorie');
          }
        } catch (error) {
          console.error('❌ [CategorySearchSimulator] Erreur lors de l\'application:', error);
          onError?.(error.message);
        }
      }, 100);

      // 🧹 NETTOYAGE DE L'URL (optionnel)
      // Supprimer les paramètres temporaires pour nettoyer l'URL
      const cleanParams = new URLSearchParams(location.search);
      cleanParams.delete('applySavedSearch');
      cleanParams.delete('categoryId');
      cleanParams.delete('categoryName');
      
      const newSearch = cleanParams.toString();
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
      
      // Mettre à jour l'URL sans recharger (après un délai pour éviter les conflits)
      setTimeout(() => {
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', newUrl);
          console.log('🧹 [CategorySearchSimulator] URL nettoyée:', newUrl);
        }
      }, 500);
    }
  }, [location.search, onApplySearch, onError]);

  // Composant invisible - ne rend rien
  return null;
};

export default CategorySearchSimulator;
